import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ExternalLink, Trash2, Layout } from 'lucide-react';
import type { FloorPlan, CreateFloorPlanInput } from '@peles/shared';
import { EntityList } from '../common';
import { EntityModal } from '../common/EntityModal';
import type { EntityListColumn } from '../common';
import {
  useFloorPlans,
  useCreateFloorPlan,
  useDeleteFloorPlan,
  useRooms,
} from '../../hooks/useFloorPlans';
import { SearchableListSelect } from '../common/SearchableListSelect';

interface Props {
  venueId: string;
}

export function VenueFloorPlanListView({ venueId }: Props) {
  const navigate = useNavigate();
  const { data: floorPlans = [], isLoading } = useFloorPlans({ venueId });
  const { data: rooms = [] } = useRooms(venueId);
  const createFloorPlan = useCreateFloorPlan();
  const deleteFloorPlan = useDeleteFloorPlan();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRoomId, setNewRoomId] = useState('');
  const [createError, setCreateError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  function openCreate() {
    setNewName('');
    setNewRoomId('');
    setCreateError('');
    setShowCreate(true);
  }

  function closeCreate() {
    setShowCreate(false);
    setCreateError('');
  }

  async function handleCreate() {
    setCreateError('');
    const name = newName.trim();
    if (!name) {
      setCreateError('Name is required.');
      return;
    }

    const input: CreateFloorPlanInput = { name };
    if (newRoomId) input.roomId = newRoomId;

    try {
      const plan = await createFloorPlan.mutateAsync(input);
      closeCreate();
      navigate(`/floor-plans?planId=${plan.id}&fromVenueId=${venueId}`);
    } catch {
      setCreateError('Failed to create floor plan. Please try again.');
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteFloorPlan.mutateAsync(id);
    } finally {
      setDeletingId(null);
    }
  }

  function formatDate(date: Date | string) {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const columns: EntityListColumn<FloorPlan>[] = [
    {
      key: 'name',
      header: 'Floor Plan',
      sortable: true,
      sortValue: (fp) => fp.name,
      render: (fp) => (
        <div className="flex items-center gap-2">
          <Layout size={14} className="text-gray-400 flex-shrink-0" />
          <span className="font-medium text-gray-900">{fp.name}</span>
          {fp.isTemplate && (
            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
              Template
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'event',
      header: 'Event',
      hideOnMobile: true,
      render: (fp) => <span className="text-sm text-gray-600">{fp.event?.name ?? '—'}</span>,
    },
    {
      key: 'room',
      header: 'Room',
      hideOnMobile: true,
      render: (fp) => <span className="text-sm text-gray-600">{fp.room?.name ?? '—'}</span>,
    },
    {
      key: 'seats',
      header: 'Seats',
      hideOnMobile: true,
      render: (fp) => <span className="text-sm text-gray-600">{fp.totalSeats ?? 0}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      hideOnMobile: true,
      sortable: true,
      sortValue: (fp) => new Date(fp.createdAt).getTime(),
      render: (fp) => <span className="text-sm text-gray-500">{formatDate(fp.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: 'w-24',
      align: 'right',
      render: (fp) => (
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/floor-plans?planId=${fp.id}&fromVenueId=${venueId}`);
            }}
            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            title="Open floor plan"
          >
            <ExternalLink size={14} />
          </button>
          {deletingId === fp.id ? (
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(fp.id);
                }}
                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeletingId(null);
                }}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeletingId(fp.id);
              }}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete floor plan"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <EntityList<FloorPlan>
        title="Floor Plans"
        items={floorPlans}
        columns={columns}
        getKey={(fp) => fp.id}
        isLoading={isLoading}
        onRowClick={(fp) => navigate(`/floor-plans?planId=${fp.id}&fromVenueId=${venueId}`)}
        emptyMessage="No floor plans yet. Create one to get started."
        emptyIcon={<Layout size={32} className="text-gray-300" />}
        titleActions={
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={15} />
            New Floor Plan
          </button>
        }
      />

      <EntityModal open={showCreate} onClose={closeCreate} title="New Floor Plan" size="sm">
        <div className="space-y-4">
          {createError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {createError}
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Main Hall Layout"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>

          {rooms.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
              <SearchableListSelect
                items={rooms}
                getValue={(r) => r.id}
                getLabel={(r) => r.name}
                value={newRoomId}
                onChange={(v) => setNewRoomId(v as string)}
                placeholder="Select a room (optional)"
                clearable
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={closeCreate}
              disabled={createFloorPlan.isPending}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={createFloorPlan.isPending}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createFloorPlan.isPending ? 'Creating…' : 'Create & Open'}
            </button>
          </div>
        </div>
      </EntityModal>
    </>
  );
}
